import React from 'react';
import {
  FaChevronRight,
  FaChevronLeft,
  FaClipboardList,
  FaStepForward
} from 'react-icons/fa';

const ModalNavigationButtons = ({
  showAchievementView,
  isPathCompletedLocal,
  getDisplayedContentLocal,
  modalNavigation,
  navigationContext,
  handleBackNavigationLocal,
  handleSaveAndQuit,
  getCurrentSubmissionStatus,
  getDisplayedContentTypeLocal,
  handleSubmitSubmission,
  userCurrentContent,
  submissionData,
  urlError,
  handleForwardBrowsingLocal,
  handleNextContent,
  nextContentLoading,
  progressLoading,
  isContentProgressing,
  canProgressFromSubmission,
  currentContentIndex,
  getNextButtonText,
  modalCourseContentLength,
  userSubmissionStatus
}) => {
  if (showAchievementView || isPathCompletedLocal()) {
    return null;
  }

  const isAnyLoadingActive = nextContentLoading || progressLoading || isContentProgressing;

  const actualGlobalIndex = navigationContext?.currentGlobalIndex ?? -1;
  const showReturnToProgress = modalNavigation.isViewingBackwards &&
    actualGlobalIndex >= 0 &&
    modalNavigation.globalIndex >= actualGlobalIndex - 1;

  const canGoBack = getDisplayedContentLocal() && (
    modalNavigation.isViewingBackwards
      ? modalNavigation.canNavigateBack
      : (navigationContext?.currentGlobalIndex || 0) > 0
  );

  const submissionStatus = getCurrentSubmissionStatus();
  // When viewing backwards, look up status by displayed content ID
  const displayedContent = getDisplayedContentLocal();
  const backwardsSubmissionStatus = modalNavigation.isViewingBackwards && displayedContent
    ? userSubmissionStatus?.[displayedContent._id] : null;
  const effectiveStatus = modalNavigation.isViewingBackwards ? backwardsSubmissionStatus : submissionStatus;

  const shouldShowSubmitButton = getDisplayedContentTypeLocal() === 'submissionForm' &&
    (!modalNavigation.isViewingBackwards
      ? (!effectiveStatus || !effectiveStatus.hasSubmission ||
         (effectiveStatus.hasSubmission && ['rejected', 'more_info_needed'].includes(effectiveStatus.submission.status)))
      : (effectiveStatus?.hasSubmission && ['rejected', 'more_info_needed'].includes(effectiveStatus.submission.status))
    );

  const activeContent = modalNavigation.isViewingBackwards ? displayedContent : userCurrentContent;
  const isSubmitDisabled =
    (activeContent?.linkRequired && !submissionData.link.trim()) ||
    (activeContent?.commentRequired && !submissionData.comment.trim()) ||
    (activeContent?.achievementImageRequired && (!submissionData.achievementImages || submissionData.achievementImages.length === 0)) ||
    urlError;

  const nextButtonLabel = (() => {
    if (modalNavigation.isViewingBackwards) {
      return showReturnToProgress ? 'Return to Progress' : 'Next';
    }
    if (getDisplayedContentTypeLocal() === 'submissionForm' && submissionStatus?.hasSubmission) {
      if (submissionStatus.submission.status === 'approved') return 'Continue';
      if (submissionStatus.submission.status === 'pending') return 'Awaiting Approval';
      if (submissionStatus.submission.status === 'rejected') return 'Resubmit Required';
      if (submissionStatus.submission.status === 'more_info_needed') return 'Update Required';
    }
    return getNextButtonText();
  })();

  const isNextDisabled = isAnyLoadingActive ||
    (!modalNavigation.isViewingBackwards && (!userCurrentContent || !canProgressFromSubmission()));

  return (
    /* Fixed bottom toolbar — replaces scattered FABs */
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t border-border">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">

        {/* Left: Back button */}
        <div className="flex-1 flex justify-start">
          {canGoBack && (
            <button
              onClick={handleBackNavigationLocal}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
            >
              <FaChevronLeft className="w-3 h-3" />
              <span className="hidden sm:inline">Previous</span>
            </button>
          )}
        </div>

        {/* Center: Progress indicator */}
        {getDisplayedContentLocal() && (
          <span className="text-sm text-muted-foreground font-medium tabular-nums flex-shrink-0">
            {currentContentIndex + 1} / {modalCourseContentLength}
          </span>
        )}

        {/* Right: Save & Quit + primary action */}
        <div className="flex-1 flex justify-end items-center gap-2">
          {getDisplayedContentLocal() && (
            <button
              onClick={handleSaveAndQuit}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
            >
              <span>Save & Quit</span>
            </button>
          )}

          {/* Primary action */}
          {shouldShowSubmitButton ? (
            <button
              onClick={() => handleSubmitSubmission(modalNavigation.isViewingBackwards ? displayedContent : undefined)}
              disabled={isSubmitDisabled}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                isSubmitDisabled
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-mission text-mission-foreground hover:opacity-90'
              }`}
            >
              <FaClipboardList className="w-3.5 h-3.5" />
              <span>Submit Mission</span>
            </button>
          ) : (
            <button
              onClick={modalNavigation.isViewingBackwards ? handleForwardBrowsingLocal : handleNextContent}
              disabled={isNextDisabled}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                isNextDisabled
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : modalNavigation.isViewingBackwards
                    ? 'bg-secondary text-secondary-foreground hover:bg-accent'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              {isAnyLoadingActive ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  {modalNavigation.isViewingBackwards && showReturnToProgress
                    ? <FaStepForward className="w-3.5 h-3.5" />
                    : null
                  }
                  <span>{nextButtonLabel}</span>
                  {!showReturnToProgress && <FaChevronRight className="w-3 h-3" />}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalNavigationButtons;
